import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { BotonPreguntaleAtlas } from "../../chat-atlas";
import { useBrandProfile } from "../../hooks/useBrandProfile";
import { leerMarca } from "./datos/marcaApi";
import type { MarcaDS } from "./datos/marcaApi";
import { SeccionProximamente } from "./components/SeccionProximamente";
import { MarcaVisualPanel } from "./components/MarcaVisualPanel";
import { MarcaPerfilPanel } from "./components/MarcaPerfilPanel";
import { BannerGuardado } from "./components/BannerGuardado";
import type { MensajeGuardado } from "./components/BannerGuardado";
import { palabraDeModeloActual } from '../../lib/nombreDeModelo';

/**
 * Pantalla Marca: el design system de la marca activa — colores,
 * tipografías, logo y contexto. Primera pantalla del Estudio de marketing
 * con datos REALES (`brand_profiles`, ver `datos/marcaApi.ts`); por eso no
 * lleva `<BannerVistaEjemplo />` — ese cartel existe para avisar que un
 * número no es de la cuenta del cliente, y acá todo lo que se muestra sí lo
 * es.
 *
 * Escribe `colors`, `fonts` y `context` de `brand_profiles` — los MISMOS
 * campos que "Mi marca" → "Mi diseño" (`config_sections/CompanyData`, que
 * está en producción). La equivalencia de shape y normalización entre las dos
 * pantallas vive en `datos/marcaApi.ts`. `name`, `rubro` y `logo_url` NO se
 * escriben desde acá: tienen efectos cruzados sobre `company_profiles` y se
 * siguen editando en un solo lugar (Configuración → Mis marcas).
 *
 * El contenido real de cada tab vive en `components/MarcaVisualPanel.tsx` y
 * `components/MarcaPerfilPanel.tsx` — separado de acá para no pasar el
 * tamaño de archivo recomendado; esta pantalla solo resuelve la marca activa
 * y decide qué tab y qué estado (cargando/error/vacío) mostrar.
 */

type TabKey = "perfil" | "contenido" | "visual" | "assets";

const TABS: { key: TabKey; label: string }[] = [
  { key: "perfil", label: "Perfil" },
  { key: "contenido", label: "Contenido" },
  { key: "visual", label: "Visual" },
  { key: "assets", label: "Assets" },
];

/**
 * Envuelve el contenido de un tab con datos reales (Perfil, Visual) en los
 * cuatro `if` que decide, en orden: `cargando` (spinner), `sinMarca` (mensaje
 * fijo, sin Reintentar), `error` (bloque rojo con Reintentar) y, si ninguno
 * de los tres aplicó pero `marca` todavía es `null`, otro spinner — recién
 * después de los cuatro se llega al contenido con la marca ya resuelta. Los
 * tabs `contenido`/`assets` no pasan por acá: son `SeccionProximamente` fijo,
 * no dependen de esta consulta.
 *
 * `sinMarca` va separado de `error`: una cuenta sin ninguna marca todavía
 * (sin onboarding) no es una consulta que falló — decirle "no pudimos
 * cargar, reintentá" sería mentirle sobre qué pasó y ofrecerle un botón que
 * no arregla nada.
 *
 * El cuarto `if` (`!marca` sin `error`) existe porque `cargando` puede pasar
 * a `false` un commit ANTES de que el efecto de `MarcaPage` alcance a poner
 * `cargandoMarca` en `true` de nuevo (la cola de la carrera entre
 * `currentBrandId` llegando y el próximo flush de efectos). En ese instante
 * `error` y `marca` son los dos "falsy" a la vez — tratarlo como `error`
 * pintaría un cartel rojo que se prende y se apaga solo un commit después;
 * tratarlo como loading es honesto, porque hay una carga real en curso o a
 * punto de empezar (con `currentBrandId` ya seteado, el efecto SIEMPRE
 * termina en `marca` o `error`, nunca se queda pegado acá).
 *
 * Es una función plana (no un componente) a propósito: si fuera
 * `<EnvolverEstado>`, al ser una definición anidada dentro del render de
 * `MarcaPage`, React vería un tipo de componente nuevo en cada render y
 * desmontaría/remontaría el árbol de abajo en cada re-render sin motivo.
 */
function envolverEstadoMarca(
  cargando: boolean,
  sinMarca: boolean,
  error: boolean,
  marca: MarcaDS | null,
  onReintentar: () => void,
  render: (marca: MarcaDS) => ReactNode,
): ReactNode {
  if (cargando) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-[#02062B]/[0.45]">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-[13px]">Cargando datos de tu marca…</span>
      </div>
    );
  }
  if (sinMarca) {
    return (
      <div className="rounded-2xl border border-dashed border-[#D8DCE6] py-10 px-6 text-center">
        <p className="text-[13px] font-medium text-[#02062B]/[0.55]">
          Todavía no tenés una marca configurada.
        </p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-[#F8E3E2] bg-[#FCF3F2] py-10 px-6 text-center">
        <AlertCircle className="h-5 w-5 text-[#C33B36]" />
        <p className="max-w-sm text-[13px] font-medium text-[#C33B36]">
          No pudimos cargar los datos de tu marca. Puede ser un problema de conexión — probá de
          nuevo.
        </p>
        <button
          type="button"
          onClick={onReintentar}
          className="rounded-full bg-brand-primary py-2 px-4 text-[12px] font-medium text-white"
        >
          Reintentar
        </button>
      </div>
    );
  }
  if (!marca) {
    // `cargando` ya dio `false` pero todavía no hay `marca` NI `error`: es la
    // cola de la carrera entre el commit que apaga `cargando` (al llegar
    // `currentBrandId`) y el próximo flush de efectos, que recién ahí prende
    // `cargandoMarca` y dispara `leerMarca`. Con `currentBrandId` ya seteado,
    // ese efecto SIEMPRE termina en `marca` o `error` — nunca se queda acá.
    // Mostrar el bloque rojo en este instante sería un falso "no pudimos
    // cargar" que aparece y desaparece solo; el spinner es honesto porque
    // hay, de hecho, una carga en curso (o a punto de empezar).
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-[#02062B]/[0.45]">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-[13px]">Cargando datos de tu marca…</span>
      </div>
    );
  }
  return render(marca);
}

export const MarcaPage = () => {
  const {
    profile,
    currentBrandId,
    loading: cargandoPerfil,
    loadingBrands,
    refresh: refrescarPerfil,
  } = useBrandProfile();
  const [tab, setTab] = useState<TabKey>("visual");
  const [marca, setMarca] = useState<MarcaDS | null>(null);
  const [cargandoMarca, setCargandoMarca] = useState(true);
  const [error, setError] = useState(false);
  const [intento, setIntento] = useState(0);
  const [mensaje, setMensaje] = useState<MensajeGuardado | null>(null);

  // Carga (y recarga con "Reintentar") el design system de la marca activa.
  // Se espera a `cargandoPerfil` (perfil) Y `loadingBrands` (marcas de la
  // empresa) — son dos pasos async separados en `useBrandProfile`, y solo
  // cuando los dos terminaron se puede confiar en que `currentBrandId` en
  // null significa "esta cuenta no tiene marca" y no "todavía no llegó la
  // respuesta". `cancelado` evita fugas de estado: si el usuario cambia de
  // marca o navega fuera de la pantalla antes de que vuelva la consulta, la
  // respuesta vieja no pisa el estado nuevo.
  useEffect(() => {
    if (cargandoPerfil || loadingBrands) return;
    if (!currentBrandId) {
      setCargandoMarca(false);
      setError(false);
      setMarca(null);
      return;
    }
    let cancelado = false;
    setCargandoMarca(true);
    setError(false);
    setMarca(null);
    leerMarca(currentBrandId)
      .then((datos) => {
        if (cancelado) return;
        if (!datos) {
          setError(true);
          return;
        }
        setMarca(datos);
      })
      .catch(() => {
        if (!cancelado) setError(true);
      })
      .finally(() => {
        if (!cancelado) setCargandoMarca(false);
      });
    return () => {
      cancelado = true;
    };
  }, [currentBrandId, cargandoPerfil, loadingBrands, intento]);

  // El mensaje de guardado es de UNA marca: si el usuario cambia de marca, deja
  // de aplicar. Va en un efecto propio y NO adentro del de carga a propósito:
  // ese efecto se vuelve a correr después de cada guardado (el evento
  // `atlas:brand-changed` hace que `loadingBrands` pase por `true`), así que
  // limpiar ahí borraría el "Listo, guardamos" justo al mostrarlo.
  useEffect(() => {
    setMensaje(null);
  }, [currentBrandId]);

  // El éxito se apaga solo; el error se queda hasta la próxima acción.
  useEffect(() => {
    if (mensaje?.tipo !== "ok") return;
    const id = window.setTimeout(() => setMensaje(null), 4000);
    return () => window.clearTimeout(id);
  }, [mensaje]);

  /**
   * Algo se escribió en `brand_profiles`. Se avisa al resto de la app con el
   * evento que ya escuchan `useBrandProfile`, `Sidebar`, `InterfazV2` y
   * compañía para releer la marca — si no, la paleta y la tipografía nuevas no
   * aparecerían en el generador hasta recargar la página.
   *
   * Va separado del cartel (`alGuardar`) porque no son la misma pregunta: el
   * evento depende de si la BASE cambió, y hay que emitirlo incluso cuando la
   * tanda salió a medias (una columna guardada y la otra no) o cuando el
   * usuario ya se fue de la marca. El cartel, en cambio, solo tiene sentido si
   * el panel sigue vigente.
   *
   * Va sin `detail.brandId` (a diferencia del switch de marca): acá la marca
   * activa NO cambió, se editó la que ya estaba, y los dos únicos consumidores
   * que leen el detail lo hacen con guarda (`if (brandId)`), así que para ellos
   * es un no-op — que es lo correcto.
   */
  const alEscribirMarca = () => window.dispatchEvent(new Event("atlas:brand-changed"));

  const alGuardar = (texto: string) => setMensaje({ tipo: "ok", texto });

  const alFallarGuardado = (texto: string) => setMensaje({ tipo: "error", texto });

  const cargando = cargandoPerfil || loadingBrands || cargandoMarca;
  // El perfil puede terminar de cargar (`cargandoPerfil = false`) y quedar en
  // `null` igual: pasa cuando `fetchProfile` pega un error de red/auth o no
  // encuentra el usuario (`useBrandProfile.ts`, los `return` tempranos antes
  // de `setProfile`). Eso es una FALLA, no "esta cuenta no tiene marca" — si
  // se tratara igual que `sinMarca`, sería la misma mentira que ya se evitó
  // en `leerMarca` (mostrar vacío cuando en realidad la consulta no volvió).
  const perfilFallo = !cargandoPerfil && profile === null;
  const errorGeneral = error || perfilFallo;
  // Solo es "sin marca" una vez que dejó de cargar y el perfil SÍ llegó bien:
  // mientras `cargando` es true, o si hubo una falla real, nunca se llega a
  // mostrar este mensaje.
  const sinMarca = !cargando && !errorGeneral && !currentBrandId;
  // Reintenta las dos cosas que pudieron haber fallado: el perfil/marcas del
  // hook compartido (`refrescarPerfil`, cubre `perfilFallo`) y la consulta
  // propia de esta pantalla (`intento`, cubre el error de `leerMarca`).
  const reintentar = () => {
    refrescarPerfil();
    setIntento((n) => n + 1);
  };

  return (
    <div className="p-8">
      {/* El botón va en la fila del título, arriba de las pestañas: no tapa
          ningún campo editable de los paneles de abajo. */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[22px] font-semibold text-brand-black">Marca</h1>
          <p className="mt-1 text-[13px] text-[#02062B]/[0.55]">Lo que Atlas sabe de tu marca</p>
        </div>
        <BotonPreguntaleAtlas
          pantalla="Marca del estudio de marketing"
          detalle={`Pestaña "${TABS.find((t) => t.key === tab)?.label ?? "Perfil"}" del design system de la marca.`}
        />
      </div>

      <div className="mt-6 flex items-center gap-1 border-b border-[#02062B]/[0.08]">
        {TABS.map((t) => {
          const activo = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                setTab(t.key);
                // El cartel es del guardado de la tab que se está dejando.
                setMensaje(null);
              }}
              className={`-mb-px border-b-2 py-2.5 px-4 text-[13px] font-medium transition-colors ${
                activo
                  ? "border-brand-primary text-brand-primary"
                  : "border-transparent text-[#02062B]/[0.5] hover:text-[#02062B]/[0.8]"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <BannerGuardado mensaje={mensaje} />

      <div className="mt-6">
        {tab === "perfil" &&
          envolverEstadoMarca(cargando, sinMarca, errorGeneral, marca, reintentar, (m) => (
            // `key` por marca: si la marca cambia, el panel se REMONTA en vez
            // de reusarse. Sin eso, un borrador a medio editar (o un guardado
            // en vuelo) podría quedar colgado sobre la marca nueva.
            <MarcaPerfilPanel
              key={m.id}
              marca={m}
              onMarcaEscrita={alEscribirMarca}
              onGuardado={alGuardar}
              onError={alFallarGuardado}
            />
          ))}

        {tab === "contenido" && (
          <SeccionProximamente
            titulo="Contenido"
            descripcion="Acá vas a ver los ángulos y personas de contenido con los que trabaja esta marca — a quién le hablás y con qué enfoque. Sale de la memoria de la empresa, todavía sin construir (Bloque B)."
          />
        )}

        {tab === "visual" &&
          envolverEstadoMarca(cargando, sinMarca, errorGeneral, marca, reintentar, (m) => (
            <MarcaVisualPanel
              key={m.id}
              marca={m}
              onMarcaEscrita={alEscribirMarca}
              onGuardado={alGuardar}
              onError={alFallarGuardado}
            />
          ))}

        {tab === "assets" && (
          <SeccionProximamente
            titulo="Assets"
            descripcion={`Acá vas a ver el elenco y los assets reutilizables de la marca: ${palabraDeModeloActual().plural}, poses y fotos base para generar contenido nuevo. Sale de la memoria de la empresa, todavía sin construir (Bloque B).`}
          />
        )}
      </div>
    </div>
  );
};

export default MarcaPage;
